import React, { createContext, useContext, useEffect, useState } from 'react';
import io from 'socket.io-client';
import { resolveSocketOrigin } from '../shared/config/api';
import { authService } from '../shared/services/authService';
import { bindCommunitySocket } from '../features/community/services/communitySocketService';

interface SocketContextType {
  socket: ReturnType<typeof io> | null;
  isConnected: boolean;
}

const SocketContext = createContext<SocketContextType>({
  socket: null,
  isConnected: false,
});

export const useSocket = () => useContext(SocketContext);

export const SocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [socket, setSocket] = useState<ReturnType<typeof io> | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    const token = authService.getToken();
    const userId = localStorage.getItem('userId') || undefined;
    const rawUser = localStorage.getItem('user');
    let agentName = 'Agent';
    if (rawUser) {
      try {
        const parsed = JSON.parse(rawUser) as { firstName?: string; lastName?: string; username?: string };
        agentName =
          [parsed.firstName, parsed.lastName].filter(Boolean).join(' ').trim() ||
          parsed.username ||
          agentName;
      } catch {
        // ignore malformed user cache
      }
    }

    const socketInstance = io(resolveSocketOrigin(), {
      transports: ['websocket', 'polling'],
      autoConnect: true,
      auth: token ? { token } : undefined,
    });

    const identify = () => {
      if (!userId) return;
      socketInstance.emit('agent:identify', {
        userId,
        agentName,
        clientType: 'web',
      });
    };

    socketInstance.on('connect', () => {
      setIsConnected(true);
      identify();
    });

    socketInstance.on('reconnect', () => {
      setIsConnected(true);
      identify();
    });

    socketInstance.on('disconnect', () => {
      setIsConnected(false);
    });

    bindCommunitySocket(socketInstance);
    setSocket(socketInstance);

    return () => {
      bindCommunitySocket(null);
      socketInstance.disconnect();
    };
  }, []);

  return (
    <SocketContext.Provider value={{ socket, isConnected }}>
      {children}
    </SocketContext.Provider>
  );
};

export { SocketContext };
